import { Injectable, OnDestroy, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Subscription, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EventBusService } from './event-bus.service';
import { AppUpdateAvailableEvent } from '../models/events';

/**
 * Detects when a new deployment is available and lets the user switch to it **via the update banner**
 * (no automatic reload — the user keeps control and is never interrupted mid-work).
 *
 * The Angular service worker is offline-first: once registered it serves the cached shell, then
 * downloads a new version in the background and emits `VERSION_READY`. We surface that through
 * {@link updateAvailable$} so the banner can offer a reload; applying it activates the freshly
 * downloaded version and reloads onto it. For the banner to appear reliably, the SW control files
 * (ngsw.json / ngsw-worker.js) must be served with `no-cache` — see spa/public/server.js.
 *
 * Detection is sped up by: an explicit SignalR "new version deployed" push from CI
 * (`AppUpdateAvailableEvent` → forces a check, so the banner only shows once a version is actually
 * ready to activate), a 5-minute timer, tab focus, and an initial check at startup.
 *
 * Applying the update preserves the Auth0 session (kept in localStorage, `cacheLocation:
 * 'localstorage'`) — we deliberately do NOT clear site data, which would log the user out.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  // Periodic safety check; ngsw also reacts to tab focus (see below).
  private readonly checkIntervalMs = 5 * 60 * 1000;
  private checkTimer: ReturnType<typeof setInterval> | null = null;
  private isApplyingUpdate = false;

  private readonly updateAvailableSubject = new BehaviorSubject<boolean>(false);
  readonly updateAvailable$ = this.updateAvailableSubject.asObservable();

  private readonly swUpdate = inject(SwUpdate);
  private readonly eventBus = inject(EventBusService);
  private readonly sub = new Subscription();

  constructor() {
    // CI pushes this right after a deploy → check immediately instead of waiting for the timer. The
    // banner then appears via VERSION_READY, i.e. only once a new version is actually downloaded and
    // ready to activate (so a banner click always lands on the new version).
    this.sub.add(
      this.eventBus.observe(AppUpdateAvailableEvent).subscribe(() => this.checkForUpdate()),
    );

    if (!this.swUpdate.isEnabled) {
      // Dev mode / unsupported browser — no service worker, nothing to poll.
      return;
    }

    // A new version finished downloading and is ready to be activated → offer it via the banner.
    this.sub.add(
      this.swUpdate.versionUpdates
        .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
        .subscribe(() => this.markUpdateAvailable()),
    );

    // SW cache is unrecoverable (e.g. cached hashed files 404 after a deploy) → offer a reload to
    // recover via the banner.
    this.sub.add(this.swUpdate.unrecoverable.subscribe(() => this.markUpdateAvailable()));

    // Check on a timer and whenever the tab regains focus, so a user that leaves the app open all
    // day still notices a deployment quickly.
    this.checkTimer = setInterval(() => this.checkForUpdate(), this.checkIntervalMs);
    this.sub.add(
      fromEvent(window, 'focus').subscribe(() => this.checkForUpdate()),
    );

    this.checkForUpdate();
  }

  /**
   * Activate the downloaded version and reload onto it. Called from the update banner's button —
   * the reload happens only on this explicit user action.
   */
  async applyUpdate(): Promise<void> {
    if (this.isApplyingUpdate) {
      return;
    }
    this.isApplyingUpdate = true;

    try {
      if (this.swUpdate.isEnabled) {
        // Swap the active service worker to the new version before reloading, otherwise the reload
        // would be served the old cached shell again.
        await this.swUpdate.activateUpdate();
      }
    } catch {
      // Fall through to a reload even if activation failed.
    }

    window.location.reload();
  }

  /**
   * A lazy chunk of the running build no longer exists on the server — the tab has been open across
   * a deployment. Surface the same banner as a service-worker-detected update: the loaded code is
   * stale and only a reload fixes it. Needed independently of {@link swUpdate} because the DEV build
   * ships without a service worker (angular.json: only the production configuration sets
   * `serviceWorker`), so nothing else would ever notice there.
   */
  reportStaleBundle(): void {
    this.markUpdateAvailable();
  }

  private checkForUpdate(): void {
    if (!this.swUpdate.isEnabled || this.updateAvailableSubject.getValue()) {
      return;
    }
    // Errors here are non-fatal (offline, transient) — ignore them.
    this.swUpdate.checkForUpdate().catch(() => undefined);
  }

  private markUpdateAvailable(): void {
    if (!this.updateAvailableSubject.getValue()) {
      this.updateAvailableSubject.next(true);
    }
  }

  ngOnDestroy(): void {
    if (this.checkTimer !== null) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
    this.sub.unsubscribe();
  }
}
